import React from 'react';
import { Tooltip } from '../tooltip/tooltip';
import { Icon } from '../icon/icon';

export type BadgeColor =
  | 'default'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'pink'
  | 'red'
  | 'gray'
  | 'light-gray'
  | 'teal';

type Props = {
  className?: string;
  boldLabel?: string;
  label: string;
  theme?: 'primary' | 'secondary';
  color?: BadgeColor;
  size?: 'small' | 'medium' | 'large';
  leadingIcon?: string;
  trailingIcon?: string;
  allCaps?: boolean;
  tooltip?: string;
  closeIcon?: boolean;
  onDelete?: () => void;
  'data-test-id'?: string;
};
export const Badge = ({
  color = 'default',
  theme = 'primary',
  size = 'medium',
  className = '',
  allCaps = false,
  boldLabel,
  label,
  tooltip,
  leadingIcon,
  closeIcon = false,
  onDelete,
  'data-test-id': dataTestId,
}: Props) => {
  const tw = {
    badge:
      'flex items-center justify-center rounded-full whitespace-nowrap w-fit' +
      (allCaps ? ' uppercase font-semibold! ' : ' font-normal ') +
      (closeIcon ? 'pr-1! ' : ''),
    small: 'h-5 text-xs leading-4 px-2',
    medium: 'h-6 text-sm leading-5 py-0 px-3',
    large: 'h-7 text-base leading-6 px-4',
    default:
      theme === 'primary'
        ? ' bg-gray-700 text-white'
        : ' border border-gray-400 text-gray-400',
    blue:
      theme === 'primary'
        ? ' bg-rezi-blue-200 text-rezi-blue-900'
        : ' border border-rezi-blue-200 text-rezi-blue-200',
    green:
      theme === 'primary'
        ? ' bg-green-200 text-green-900'
        : ' border border-green-200 text-green-200',
    yellow:
      theme === 'primary'
        ? ' bg-amber-200 text-amber-900'
        : ' border border-amber-200 text-amber-200',
    pink:
      theme === 'primary'
        ? ' bg-pink-200 text-pink-900'
        : ' border border-pink-200 text-pink-200',
    red:
      theme === 'primary'
        ? ' bg-red-200 text-red-900'
        : ' border border-red-200 text-red-200',
    gray:
      theme === 'primary'
        ? ' bg-slate-500 text-neutral-0'
        : ' border border-slate-500 text-slate-500',
    teal:
      theme === 'primary'
        ? ' bg-teal-200 text-teal-900'
        : ' border border-teal-200 text-teal-200',
    'light-gray':
      theme === 'primary'
        ? ' bg-slate-300 text-gray-900'
        : ' border border-choice-list-item-stroke text-gray-900 bg-choice-list-item-normal',
  };
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  return (
    <div className={`${className}`}>
      <div
        data-test-id={dataTestId}
        className={tw.badge + tw[size] + tw[color] + ' ' + className}
        ref={tooltipRef}
      >
        {leadingIcon && (
          <Icon
            icon={leadingIcon}
            className="mr-1"
            size="xs"
            type="static"
            noHover
          />
        )}
        {boldLabel && <span className="font-semibold">{boldLabel}</span>}
        {label}
        {tooltip && (
          <Tooltip
            className="invisible group-hover/item:visible"
            direction="top"
            targetRef={tooltipRef}
          >
            {tooltip}
          </Tooltip>
        )}
        {closeIcon && (
          <Icon
            icon="fad fa-circle-xmark"
            className="ml-1"
            size="small"
            type="interactive"
            onClick={onDelete}
          />
        )}
      </div>
    </div>
  );
};
